import { useEffect, useState } from 'react'
import { ErroDaApi } from '../../../api/client'
import { useEditarPlano, type EdicaoDoPlano } from '../../../api/queries/gmuds'
import { AvisoDeErro } from '../../../components/Estados'
import { Cartao } from '../../../components/ListaDeDados'
import { CAMPOS_DO_PLANO } from '../../../lib/glossario'
import type { CampoDoPlano, DetalheGmud } from '../../../types/gmud'
import { CampoDoPlanoEditavel } from './CampoDoPlanoEditavel'
import { ResumoDePendencias } from './ResumoDePendencias'

/**
 * O plano da etapa 1 do ANEXO XXI, com o que falta em cima e a correção no próprio lugar.
 *
 * **A tela não decide se o plano pode ser editado.** Quem decide é `Gmud.alterarPlano`, que
 * recusa a alteração depois de a solicitação ter ido ao comitê — o que foi aprovado tem de ser o
 * que for executado. Aqui se tenta o PATCH e se trata a recusa: quando ela vem com
 * `GMUD_PLANO_CONGELADO`, a mensagem do servidor sobe para o topo do cartão (ela já explica que
 * é preciso pedir ao comitê para devolver a solicitação) e os botões de editar param de ser
 * oferecidos no resto da visita. Reagir ao CÓDIGO e nunca ao texto é o contrato do `client.ts`.
 *
 * O único palpite que a tela dá por conta própria é `origem`: em registro importado não há plano
 * deste processo para corrigir, e o servidor recusaria de todo jeito. É pista de interface, não
 * autorização — o botão escondido não protege nada, quem protege é o domínio.
 *
 * Salva um campo por vez porque o PATCH mescla: campo ausente mantém o que está gravado. Assim
 * duas pessoas mexendo em artefatos diferentes não sobrescrevem uma à outra, que é o que
 * aconteceria se cada salvamento reenviasse o plano inteiro.
 *
 * @param props a solicitação
 * @returns a aba do plano
 */
export function AbaDoPlano({ gmud }: { gmud: DetalheGmud }) {
  const editar = useEditarPlano(gmud.id)
  const [emEdicao, setEmEdicao] = useState<CampoDoPlano | null>(null)
  // O congelamento acompanha o status. A recusa do servidor chega com a tela ainda no status
  // ANTIGO (foi por isso que ele recusou), então a PRIMEIRA mudança de status que o refetch
  // trouxer é o próprio status congelado — o marcador a acompanha e segue congelado. Só a
  // mudança SEGUINTE (o comitê devolveu para ajustes, por exemplo) reabre a edição. Antes,
  // um boolean solto escondia os botões até a pessoa recarregar a página.
  const [congeladoEm, setCongeladoEm] = useState<{ status: string; confirmado: boolean } | null>(null)
  useEffect(() => {
    setCongeladoEm((atual) => {
      if (!atual || atual.status === gmud.status) {
        return atual
      }
      return atual.confirmado ? null : { status: gmud.status, confirmado: true }
    })
  }, [gmud.status])
  const congelado = congeladoEm !== null

  const editavel = gmud.origem !== 'MIGRADO_V1' && !congelado

  async function salvar(campo: CampoDoPlano, texto: string) {
    const alteracao: EdicaoDoPlano = {}
    alteracao[campo] = texto
    try {
      await editar.mutateAsync(alteracao)
      setEmEdicao(null)
    } catch (erro) {
      if (erro instanceof ErroDaApi && erro.codigo === 'GMUD_PLANO_CONGELADO') {
        setCongeladoEm({ status: gmud.status, confirmado: false })
        setEmEdicao(null)
      }
      // Nas demais recusas o campo continua aberto, com o texto digitado e o erro do servidor
      // logo abaixo dele: fechar aqui apagaria o que a pessoa acabou de escrever.
    }
  }

  return (
    <Cartao
      titulo="Plano da mudança"
      descricao="É o que o comitê lê para decidir. Escreva para quem não acompanha o dia a dia do sistema."
    >
      <div className="space-y-5">
        <ResumoDePendencias pendencias={gmud.pendencias} />

        {congelado && <AvisoDeErro erro={editar.error} titulo="A alteração não foi aceita" />}

        <div className="space-y-5">
          {CAMPOS_DO_PLANO.map((campo) => (
            <CampoDoPlanoEditavel
              key={campo.campo}
              rotulo={campo.rotulo}
              ajuda={campo.ajuda}
              valor={gmud.plano[campo.campo]}
              editavel={editavel}
              editando={emEdicao === campo.campo}
              salvando={editar.isPending}
              erro={emEdicao === campo.campo && editar.isError && !congelado ? editar.error : null}
              aoEditar={() => {
                editar.reset()
                setEmEdicao(campo.campo)
              }}
              aoCancelar={() => {
                editar.reset()
                setEmEdicao(null)
              }}
              aoSalvar={(texto) => void salvar(campo.campo, texto)}
            />
          ))}
        </div>
      </div>
    </Cartao>
  )
}
